import { useState, useEffect, useMemo } from 'react';
import * as Yup from 'yup';
import Select from 'react-select';
import { Ic } from '../../shared/component/GosIcon';

/* Add / Edit UI Permission — GrowthOS-styled modal (centered .gos-import-* shell,
   themed react-select + inputs + switch). The CHROME was rebuilt from the legacy
   Formik/react-bootstrap Dropdown form; ALL logic is unchanged — default type =
   Menu, the permission-parent list filtered by type (Menu/Screen/Field) + module,
   module/type change resets in add mode, module/type/parent/value locked in edit
   mode (only Display Name + Active editable), Yup validation, and the add/edit
   payload handed to handleFormSubmit exactly as before. */

const validationSchema = Yup.object().shape({
  permissionValue: Yup.string().required('Permission value is required'),
  permissionDisplayName: Yup.string().required('Name is required'),
  permissionTypeId: Yup.string().required('Permission type is required'),
  moduleId: Yup.string().required('Module is required'),
});

const gosSelect = (disabled) => ({
  control: (base, s) => ({
    ...base, minHeight: 42, borderRadius: 10, fontSize: 13.5,
    borderColor: s.isFocused ? 'var(--gos-accent)' : 'var(--gos-border-strong)',
    boxShadow: s.isFocused ? '0 0 0 3px var(--gos-accent-soft)' : 'none',
    backgroundColor: disabled ? 'var(--gos-surface-3)' : 'var(--gos-surface)', '&:hover': { borderColor: 'var(--gos-accent)' },
  }),
  menu: (b) => ({ ...b, zIndex: 9999, backgroundColor: 'var(--gos-surface)', border: '1px solid var(--gos-border-strong)', borderRadius: 11, overflow: 'hidden' }),
  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
  option: (b, s) => ({ ...b, fontSize: 13.5, backgroundColor: s.isSelected ? 'var(--gos-accent-soft)' : s.isFocused ? 'var(--gos-surface-3)' : 'transparent', color: s.isSelected ? 'var(--gos-accent-ink)' : 'var(--gos-text)' }),
  placeholder: (b) => ({ ...b, color: 'var(--gos-text-3)' }),
  singleValue: (b) => ({ ...b, color: 'var(--gos-text)' }),
  input: (b) => ({ ...b, color: 'var(--gos-text)' }),
  indicatorSeparator: (b) => ({ ...b, backgroundColor: 'var(--gos-border)' }),
  dropdownIndicator: (b) => ({ ...b, color: 'var(--gos-text-3)' }),
});

const reqStar = <span style={{ color: 'var(--gos-red-fg)' }}> *</span>;
const errText = (msg) => (msg ? <div style={{ color: 'var(--gos-red-fg)', fontSize: 12, marginTop: 5 }}>{msg}</div> : null);

const MiniSwitch = ({ on, disabled, onClick }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    aria-pressed={!!on}
    disabled={disabled}
    style={{
      width: 40, height: 22, borderRadius: 999, border: 'none', cursor: disabled ? 'default' : 'pointer', padding: 0,
      background: on ? '#2c7a45' : 'var(--gos-border-strong)', position: 'relative', transition: 'background .15s', opacity: disabled ? 0.55 : 1,
    }}
  >
    <span style={{ position: 'absolute', top: 2, left: on ? 20 : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
  </button>
);

const AddUiPermissionsForm = ({
  handleCloseAddForm,
  handleFormSubmit,
  uiPermissionData,
  permissionType,
  open,
  permissionModule,
  permissionParent,
}) => {
  const [formData, setFormData] = useState(uiPermissionData);
  const [errormsg, setErrorMsg] = useState({});
  const [formEdited, setFormEdited] = useState(false);
  const [filteredPermissionParent, setFilteredPermissionParent] = useState([]);

  const isEdit = formData?.type === 'edit';

  useEffect(() => {
    if (!formData?.permissionTypeId) {
      const activePermissionType = permissionType?.find((pt) => pt.displayName === 'Menu');
      if (activePermissionType) {
        setFormData((p) => ({ ...p, permissionTypeId: activePermissionType.id, permissionTypeName: activePermissionType.displayName }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionType]);

  useEffect(() => {
    if (formData?.permissionTypeName === 'Menu') {
      const filteredParents = permissionParent.filter(
        (parent) =>
          parent.permissionDisplayName.toLowerCase().includes('menu') &&
          parent.moduleId === formData.moduleId &&
          parent.permissionParentId === null
      );
      setFilteredPermissionParent(filteredParents);
    } else if (formData?.permissionTypeName === 'Screen') {
      let filteredParents = permissionParent.filter(
        (parent) => parent.permissionDisplayName.toLowerCase().includes('menu') && parent.moduleId === formData.moduleId
      );
      if (filteredParents.length > 1) filteredParents = filteredParents.filter((parent) => parent.permissionParentId !== null);
      setFilteredPermissionParent(filteredParents);
    } else if (formData?.permissionTypeName === 'Field') {
      const filteredParents = permissionParent.filter(
        (parent) => parent.permissionDisplayName.toLowerCase().includes('screen') && parent.moduleId === formData.moduleId
      );
      setFilteredPermissionParent(filteredParents);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.permissionTypeName, formData?.moduleId, permissionParent]);

  const clearErr = (name) => setErrorMsg((p) => ({ ...p, [name]: undefined }));

  const onModule = (opt) => {
    clearErr('moduleId');
    const patch = { moduleId: opt?.value || '', moduleName: opt?.label || '' };
    if (formData.type === 'add') { patch.permissionTypeId = ''; patch.permissionTypeName = ''; patch.permissionParentId = ''; patch.permissionParentName = ''; }
    setFormData((p) => ({ ...p, ...patch }));
    setFormEdited(true);
  };
  const onType = (opt) => {
    clearErr('permissionTypeId');
    const patch = { permissionTypeId: opt?.value || '', permissionTypeName: opt?.label || '' };
    if (formData.type === 'add') { patch.permissionParentId = ''; patch.permissionParentName = ''; }
    setFormData((p) => ({ ...p, ...patch }));
    setFormEdited(true);
  };
  const onParent = (opt) => {
    setFormData((p) => ({ ...p, permissionParentId: opt?.value || '', permissionParentName: opt?.label || '' }));
    setFormEdited(true);
  };
  const onText = (e) => {
    const { name, value } = e.target;
    clearErr(name);
    setFormData((p) => ({ ...p, [name]: value }));
    setFormEdited(true);
  };
  const onActive = () => {
    setFormData((p) => ({ ...p, isActive: !p.isActive }));
    setFormEdited(true);
  };

  const submit = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      handleFormSubmit(formData);
    } catch (error) {
      const fieldErrors = {};
      error.inner?.forEach((err) => { fieldErrors[err.path] = err.message; });
      setErrorMsg(fieldErrors);
    }
  };

  const moduleOptions = useMemo(() => (permissionModule || []).map((m) => ({ value: m.id, label: m.displayName })), [permissionModule]);
  const typeOptions = useMemo(() => (permissionType || []).map((t) => ({ value: t.id, label: t.displayName })), [permissionType]);
  const parentOptions = useMemo(() => filteredPermissionParent.map((p) => ({ value: p.permissionId, label: p.permissionDisplayName })), [filteredPermissionParent]);

  const moduleVal = formData?.moduleId ? { value: formData.moduleId, label: formData.moduleName } : null;
  const typeVal = formData?.permissionTypeId ? { value: formData.permissionTypeId, label: formData.permissionTypeName } : null;
  const parentVal = formData?.permissionParentId ? { value: formData.permissionParentId, label: formData.permissionParentName } : null;

  if (!open) return null;

  return (
    <div className="gos-import-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) handleCloseAddForm(); }}>
      <div className="gos-import-modal" style={{ maxWidth: 560 }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="gos-import-head">
          <div className="gos-import-head-l">
            <span className="gos-import-step-ic"><Ic name="eye" size={18} /></span>
            <div>
              <div className="gos-import-title">{isEdit ? 'Edit UI permission' : 'New UI permission'}</div>
              <div className="gos-import-sub">Register a screen or menu element that roles can be granted.</div>
            </div>
          </div>
          <button type="button" className="gos-import-close" onClick={handleCloseAddForm}><Ic name="x" size={18} /></button>
        </div>

        <div className="gos-import-body" style={{ padding: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
            <div>
              <label className="gos-tm-flabel">UI Module{reqStar}</label>
              <Select options={moduleOptions} value={moduleVal} onChange={onModule} isDisabled={isEdit} placeholder="Select module" menuPortalTarget={document.body} styles={gosSelect(isEdit)} />
              {errText(errormsg.moduleId)}
            </div>
            <div>
              <label className="gos-tm-flabel">Permission type{reqStar}</label>
              <Select options={typeOptions} value={typeVal} onChange={onType} isDisabled={isEdit} placeholder="Select type" menuPortalTarget={document.body} styles={gosSelect(isEdit)} />
              {errText(errormsg.permissionTypeId)}
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label className="gos-tm-flabel">Permission parent</label>
            <Select options={parentOptions} value={parentVal} onChange={onParent} isDisabled={isEdit} isClearable placeholder="Select permission parent" menuPortalTarget={document.body} styles={gosSelect(isEdit)} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label className="gos-tm-flabel">Permission value{reqStar}</label>
            <input
              className={'gos-tm-input' + (errormsg.permissionValue ? ' err' : '')}
              name="permissionValue"
              value={formData?.permissionValue || ''}
              onChange={onText}
              disabled={isEdit}
              placeholder="e.g. TicketViewScreen"
            />
            {errText(errormsg.permissionValue)}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label className="gos-tm-flabel">Display name{reqStar}</label>
            <input
              className={'gos-tm-input' + (errormsg.permissionDisplayName ? ' err' : '')}
              name="permissionDisplayName"
              value={formData?.permissionDisplayName || ''}
              onChange={onText}
              placeholder="e.g. Ticket View Screen"
            />
            {errText(errormsg.permissionDisplayName)}
          </div>

          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span>
              <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: 'var(--gos-text)' }}>Active</span>
              <span style={{ display: 'block', fontSize: 12, color: 'var(--gos-text-3)', marginTop: 2 }}>Element is available for assignment</span>
            </span>
            <MiniSwitch on={!!formData?.isActive} disabled={!isEdit} onClick={onActive} />
          </label>
        </div>

        <div className="gos-import-foot end">
          <div className="gos-foot-actions">
            <button type="button" className="gos-btn gos-btn-ghost" onClick={handleCloseAddForm}>Cancel</button>
            <button type="button" className="gos-btn gos-btn-primary" onClick={submit} disabled={isEdit && !formEdited}>
              <Ic name="check" size={16} />{isEdit ? 'Save' : 'Add permission'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUiPermissionsForm;
